import { v4 } from "uuid";

import type { ICol, IRow, ISection } from "../../../interfaces";
import type { INMEAAutomationModalData } from "../../../interfaces/Modals";
import { deepClone } from "../../../utils";
import { convertDDMMtoMMDD } from "../../../utils/timeUtils/convertDateFormat";
import {
  clearNMEAProperties,
  clearSingleValue as clearValue,
} from "../../utils/dynamicRows/clearValues";
import { sortRowsByDateTime } from "../../utils/sortByDateTime";

export interface INMEARow {
  cols: Record<string, ICol>;
  dateTime?: string;
  dateTimeLT?: string;
  dateTimeUTC?: string;
  timeLT?: string;
  timeUTC?: string;
  NMEAId: string;
  selected?: boolean;
}

export interface INMEASection {
  rows: INMEARow[];
  originalSectionIndex: number;
}

interface IParseNMEASectionsParams {
  originalSections: ISection[];
  newData?: Array<Record<string, any>>;
  sectionIndex?: number;
  setValue?: (id: string, value: any) => void;
  rowIndex?: number;
  selectedSections?: INMEASection[];
  sortOrder?: "asc" | "desc";
  location?: "anchor" | "underway";
  preview?: boolean;
  dropRowsWithoutNMEAId?: boolean;
}

/**
 * Creates a clean row template from the first row, clearing all values
 */
const createCleanRowTemplate = (
  templateRow: IRow,
  location?: "anchor" | "underway",
): IRow => {
  const cleanRow = deepClone(templateRow);
  if (!cleanRow.cols) return cleanRow;

  const cleanCols: Record<string, ICol> = {};
  Object.entries(cleanRow.cols).forEach(([key, col]) => {
    const typedCol = col as ICol;
    const cleanCol = clearNMEAProperties(typedCol) as ICol;
    // Clear the value using clearSingleValue logic
    cleanCol.value = clearValue(col);

    // For anchor location, ensure underway fields are completely cleared
    if (location === "anchor" && typedCol.underway === true) {
      cleanCol.value = clearValue(col);
      cleanCol.location = "anchor";
    } else {
      // Clear location to ensure fresh location assignment for non-underway fields
      delete cleanCol.location;
    }
    const id = v4();
    cleanCols[id] = cleanCol;
  });

  cleanRow.cols = cleanCols;
  cleanRow.isNMEAAffected = false;
  return cleanRow;
};

/**
 * Parses NMEA values to match cell formats
 */
// Parse nmea value to match value format of the column
const parseNMEAValue = (
  typedCol: ICol,
  dataRow: Record<string, any>,
): {
  value: string | Record<string, string>;
  NMEAValue: string | Record<string, string>;
} => {
  const NMEAKey = getNMEAKey(typedCol, dataRow);

  if (NMEAKey && dataRow[NMEAKey]) {
    let newValue: any = dataRow[NMEAKey];

    // Parse nmea value to match value format of the column
    if (typedCol.unit === "date" && newValue) {
      newValue = convertDDMMtoMMDD(newValue);
    }
    if (typedCol.unit === "24lock" && newValue) {
      let splitted, time;
      if (newValue.includes("T")) {
        splitted = newValue.split("T")[1].split(":");
        time = splitted[0] + ":" + splitted[1];
      } else if (newValue.split(" ").length > 1) {
        splitted = newValue.split(" ")[3].split(":");
      } else {
        time = newValue;
      }

      newValue = {
        time,
        type: typedCol.value?.type ?? "",
      };
    }
    if (typedCol.unit === "temperature" && newValue) {
      newValue = {
        value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
        unit: "celsius",
      };
    }
    if (typedCol.unit === "pressure" && newValue) {
      newValue = {
        value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
        unit: "mbar",
      };
    }
    return { value: newValue, NMEAValue: dataRow[NMEAKey] };
  }
  return { value: "", NMEAValue: "" };
};

/**
 * Finds an existing row with the same NMEA ID
 */
const findRowByNMEAId = (
  rows: IRow[],
  nmeaId: string,
): { row: IRow; index: number } | null => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.cols) {
      const hasMatchingNMEAId = Object.values(row.cols).some(
        col => col.NMEAId === nmeaId,
      );
      if (hasMatchingNMEAId) {
        return { row, index: i };
      }
    }
  }
  return null;
};

const findNMEARowByTime = (
  rows: IRow[],
  nmeaTime: string,
): { row: IRow; index: number } | null => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.cols) {
      const hasNMEAId = Object.values(row.cols).some(col => col.NMEAId);
      const timeCol = Object.values(row.cols).find(
        col => col.NMEA === "_ROW_DATE_TIME",
      );

      if (hasNMEAId && timeCol?.value?.time === nmeaTime) {
        return { row, index: i };
      }
    }
  }
  return null;
};

const rowHasNMEAId = (row: IRow): boolean => {
  if (!row.cols) return false;
  return Object.values(row.cols).some(col => !!col.NMEAId);
};

/**
 * Updates an existing row with NMEA data
 */
const updateRowWithNMEAData = (
  existingRow: IRow,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): IRow => {
  const updatedRow = deepClone(existingRow);
  if (!updatedRow.cols) return updatedRow;

  const updatedCols: Record<string, ICol> = {};
  Object.entries(updatedRow.cols).forEach(([key, col]) => {
    const typedCol = col as ICol;

    // For existing rows, respect their individual column locations
    // Don't populate underway fields if the column already has location="anchor"
    if (typedCol.location === "anchor" && typedCol.underway === true) {
      // Keep underway columns with anchor location empty (don't populate with NMEA data)
      updatedCols[key] = typedCol;
      return;
    }

    const NMEAKey = getNMEAKey(typedCol, dataRow);

    if (typedCol.NMEA && dataRow[NMEAKey]) {
      const { value: newValue, NMEAValue } = parseNMEAValue(typedCol, dataRow);
      updatedCols[key] = {
        ...typedCol,
        value: newValue,
        source: "auto",
        NMEAId: dataRow.NMEAId,
        NMEAValue,
        ...(!typedCol.location && location && { location }),
      };
      setValue(key, newValue);
    } else {
      updatedCols[key] = typedCol;
    }
  });

  updatedRow.cols = updatedCols;
  updatedRow.isNMEAAffected = true;
  return updatedRow;
};

/**
 * Creates a new row with NMEA data
 */
const createRowWithNMEAData = (
  templateRow: IRow,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): IRow => {
  const newRow = createCleanRowTemplate(templateRow, location);
  if (!newRow.cols) return newRow;

  const newCols: Record<string, ICol> = {};
  Object.entries(newRow.cols).forEach(([key, col]) => {
    const typedCol = col;
    // Handle underway-specific columns when location is "anchor"
    if (location === "anchor" && typedCol.underway === true) {
      newCols[key] = {
        ...typedCol,
        value: clearValue(typedCol),
        location: "anchor",
      };
      setValue(key, clearValue(typedCol));
      return;
    }
    const NMEAKey = getNMEAKey(typedCol, dataRow);

    if (typedCol.NMEA && dataRow[NMEAKey]) {
      const { value: newValue, NMEAValue } = parseNMEAValue(typedCol, dataRow);
      newCols[key] = {
        ...typedCol,
        value: newValue,
        source: "auto",
        NMEAId: dataRow.NMEAId,
        NMEAValue,
        ...(location && { location }),
      };
      setValue(key, newValue);
    } else {
      newCols[key] = {
        ...typedCol,
        ...(location && { location }),
      };
      setValue(key, typedCol.value);
    }
  });

  newRow.cols = newCols;
  newRow.isNMEAAffected = true;
  return newRow;
};

interface IMergeNMEADataParams {
  section: ISection;
  sectionIndex: number;
  data: Array<Record<string, any>>;
  setValue: (id: string, value: any) => void;
  targetSectionIndex?: number;
  targetRowIndex?: number;
  sortOrder: "asc" | "desc";
  location?: "anchor" | "underway";
  dropRowsWithoutNMEAId?: boolean;
}

/**
 * Merges NMEA data with existing rows, preserving manual entries
 * Updates existing rows with matching NMEA IDs or creates new ones
 */
const mergeNMEADataWithExistingRows = ({
  section,
  sectionIndex,
  data,
  setValue,
  targetSectionIndex,
  targetRowIndex,
  sortOrder,
  location,
  dropRowsWithoutNMEAId,
}: IMergeNMEADataParams): ISection => {
  const templateRow = section.rows[0];
  if (!templateRow) return section;

  // Start with existing rows (optionally excluding placeholder rows without NMEA IDs)
  const rowsWithSaved = section.rows.map((row, index) => ({
    row,
    saved: section.savedRows?.[index] ?? false,
  }));
  const shouldDropRowsWithoutNMEAId =
    dropRowsWithoutNMEAId &&
    targetSectionIndex === undefined &&
    targetRowIndex === undefined;
  const baseRowsWithSaved = shouldDropRowsWithoutNMEAId
    ? rowsWithSaved.filter(item => rowHasNMEAId(item.row))
    : rowsWithSaved;

  const mergedRows: IRow[] = baseRowsWithSaved.map(item => item.row);
  const mergedSavedRows: boolean[] = baseRowsWithSaved.map(item => item.saved);

  // Process each NMEA data entry
  data.forEach(dataRow => {
    // Skip if this is a specific target and doesn't match
    if (
      targetSectionIndex !== undefined &&
      sectionIndex !== targetSectionIndex
    ) {
      return;
    }

    // // For specific row updates, only process the first data entry
    // if (
    //   targetSectionIndex !== undefined &&
    //   targetRowIndex !== undefined &&
    //   dataIndex > 0
    // ) {
    //   return;
    // }

    const nmeaId = dataRow.NMEAId;
    const nmeaTime = dataRow._ROW_DATE_TIME;
    if (!nmeaId) return;

    // Try to find existing row by Time or nmeaId
    const existingRowData = nmeaTime
      ? findNMEARowByTime(mergedRows, nmeaTime)
      : findRowByNMEAId(mergedRows, nmeaId);

    if (existingRowData) {
      // Update existing row – preserve its saved state
      mergedRows[existingRowData.index] = updateRowWithNMEAData(
        existingRowData.row,
        dataRow,
        setValue,
        location,
      );
    } else {
      // Create new row with NMEA data – mark as unsaved
      const newRow = createRowWithNMEAData(
        templateRow,
        dataRow,
        setValue,
        location,
      );
      mergedRows.push(newRow);
      mergedSavedRows.push(false);
    }
  });

  // Sort rows and reorder savedRows to match, using row-reference as the key
  const sortedRows = sortRowsByDateTime(
    mergedRows,
    sortOrder === "asc",
    section.dynamicRows,
  );

  const savedByRow = new Map<IRow, boolean>(
    mergedRows.map((row, i) => [row, mergedSavedRows[i] ?? true]),
  );
  const sortedSavedRows = sortedRows.map(row => savedByRow.get(row) ?? false);

  return {
    ...section,
    rows: sortedRows,
    savedRows: sortedSavedRows,
  };
};

/**
 * Sorts NMEA rows chronologically by time (0000-2359) using the dateTime property.
 * Handles INMEARow objects with dateTime property.
 *
 * @param rows - Array of NMEA rows to sort by time
 * @returns Sorted array of NMEA rows in chronological order (0000-2359)
 */
export const sortNMEARowsByTime = (rows: INMEARow[]): INMEARow[] => {
  return rows.sort((a, b) => {
    const getDateTimeValue = (row: INMEARow): number => {
      const dateTime = row?.dateTimeLT ?? row?.dateTime;
      if (!dateTime) return 0;
      try {
        const date = new Date(dateTime);
        if (!isNaN(date.getTime())) {
          return date.getTime();
        }
      } catch (error) {
        console.warn("Error parsing dateTime:", dateTime, error);
      }
      return 0;
    };
    const timeA = getDateTimeValue(a);
    const timeB = getDateTimeValue(b);
    return timeB - timeA;
  });
};

/**
 * Parses and updates the NMEA sections with new data.
 *
 * @param originalSections - The original sections to be updated.
 * @param newData - Optional new data to populate the sections.
 * @param sectionIndex - The index of the section to be updated.
 * @param setValue - Optional function to set values for specific columns.
 * @param rowIndex - The index of the row to be updated.
 * @param selectedSections - Optional selected sections to process.
 * @returns An array of updated sections.
 */
export const parseNMEASections = ({
  originalSections,
  newData,
  sectionIndex,
  setValue,
  rowIndex,
  selectedSections,
  sortOrder = "asc",
  location,
  preview,
  dropRowsWithoutNMEAId,
}: IParseNMEASectionsParams): ISection[] => {
  let data = newData;
  if (selectedSections) {
    data = selectedSections
      .flatMap(section => {
        return section.rows
          .filter(row => row !== null && row !== undefined)
          .map(row => {
            const newRow: Record<string, any> = {
              NMEAId: row.NMEAId,
              selected: row.selected,
              _TEST: true,
              _originalSectionIndex: section.originalSectionIndex, // Add section info to data
            };
            Object.entries(row.cols).forEach(([key, col]) => {
              if (col.NMEA) newRow[col.NMEA] = col.value as string;
            });
            return newRow;
          });
      })
      .filter(r => r.selected);
  }

  // Get the section indices that should be processed based on selectedSections or newData
  const targetSectionIndices = new Set<number>();
  if (selectedSections) {
    // Only add sections that have selected rows to targetSectionIndices
    selectedSections.forEach(section => {
      const hasSelectedRows = section.rows.some(row => row?.selected);
      if (hasSelectedRows) {
        targetSectionIndices.add(section.originalSectionIndex);
      }
    });
  } else if (newData) {
    // Extract originalSectionIndex from newData if available
    newData.forEach(dataRow => {
      if (dataRow._originalSectionIndex !== undefined) {
        targetSectionIndices.add(dataRow._originalSectionIndex);
      }
    });
  }

  return originalSections.map((ogSection, ogIndex) => {
    // Check if this section has any NMEA columns
    const hasNMEAColumns = ogSection.rows.some(
      row => row.cols && Object.values(row.cols).some(col => col.NMEA),
    );
    // Only process sections that:
    // 1. Have NMEA columns AND
    // 2. Are in the target indices OR when a specific sectionIndex is provided
    const shouldProcessSection =
      hasNMEAColumns &&
      (targetSectionIndices.has(ogIndex) ||
        (sectionIndex !== undefined && sectionIndex === ogIndex));

    if (!shouldProcessSection) {
      // Return section unchanged if it's not a target section
      return ogSection;
    }

    // For sections with setValue (accepting NMEA data), we need to merge with existing rows
    if (setValue && ogSection.dynamicRows && data) {
      // Filter data to only include rows that belong to this specific section
      const sectionSpecificData = data.filter(dataRow => {
        // If no section info in data, include all (fallback behavior)
        if (dataRow._originalSectionIndex === undefined) {
          return true;
        }
        // Only include data that belongs to this section
        return dataRow._originalSectionIndex === ogIndex;
      });

      // Only process if we have data for this section
      if (sectionSpecificData.length > 0) {
        return mergeNMEADataWithExistingRows({
          section: ogSection,
          sectionIndex: ogIndex,
          data: sectionSpecificData,
          setValue,
          targetSectionIndex: sectionIndex,
          targetRowIndex: rowIndex,
          sortOrder,
          location,
          dropRowsWithoutNMEAId,
        });
      } else {
        // No data for this section, return unchanged
        return ogSection;
      }
    }

    // Legacy behavior for preview/non-setValue cases
    const newRows: IRow[] = [];
    // Filter data to only include rows that belong to this specific section
    const sectionSpecificData = data?.filter(dataRow => {
      // If no section info in data, include all (fallback behavior)
      if (dataRow._originalSectionIndex === undefined) {
        return true;
      }
      // Only include data that belongs to this section
      return dataRow._originalSectionIndex === ogIndex;
    });

    const rowsMapper = (row: IRow, rowIdx: number) => {
      const newRow: IRow = deepClone(row);
      if (!newRow.cols) return row;

      const newCols: Record<string, ICol> = {};
      Object.entries(newRow.cols).forEach(([key, col]) => {
        if (
          col.NMEA &&
          ((sectionIndex !== undefined &&
            sectionIndex === ogIndex &&
            rowIndex === rowIdx) ||
            sectionIndex === undefined)
        ) {
          const dataIndex = sectionIndex !== undefined ? 0 : rowIdx;
          const effectiveLocation = col.location ?? location;
          const atAnchor = effectiveLocation === "anchor";

          // Skip underway-specific columns when location is "anchor"
          if (atAnchor && col.underway === true) {
            newCols[key] = col; // Keep original column unchanged
            return;
          }
          if (sectionSpecificData?.[dataIndex]?.[col.NMEA]) {
            let newValue: any = sectionSpecificData[dataIndex][col.NMEA];
            if (col.unit === "date" && newValue) {
              newValue = convertDDMMtoMMDD(newValue);
            }
            if (col.unit === "24lock" && newValue?.includes("T")) {
              const splitted = newValue.split("T")[1].split(":");
              const time = splitted[0] + ":" + splitted[1];
              newValue = {
                time,
                type: col.value?.type ?? "",
              };
            }
            if (col.unit === "temperature" && newValue) {
              newValue = {
                value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
                unit: "celsius",
              };
            }
            if (col.unit === "pressure" && newValue) {
              newValue = {
                value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
                unit: "mbar",
              };
            }
            const id = v4();
            newCols[id] = {
              ...col,
              value: newValue,
              source: "auto",
              NMEAId: sectionSpecificData[dataIndex].NMEAId,
              NMEAValue: sectionSpecificData[dataIndex][col.NMEA],
              ...(!col.location && location && { location: effectiveLocation }),
            };
            setValue?.(id, newValue);
            newRow.isNMEAAffected = true;
          } else {
            newCols[v4()] = { ...col };
          }
        } else {
          newCols[key] = col;
        }
      });
      if (Object.keys(newCols).length > 0) newRow.cols = newCols;

      newRows.push(newRow);
    };
    if (ogSection.dynamicRows) {
      if (!sectionSpecificData || sectionIndex !== undefined) {
        ogSection.rows.forEach(rowsMapper);
      } else {
        Array(
          sectionSpecificData.filter(el => (setValue ? el.selected : true))
            .length,
        )
          .fill(0)
          .forEach((_, rowIdx) => {
            rowsMapper(ogSection.rows[0], rowIdx);
          });
      }
    } else {
      ogSection.rows.forEach(rowsMapper);
    }
    const rowsToReturn = setValue
      ? sortRowsByDateTime(newRows, sortOrder === "asc", ogSection.dynamicRows)
      : newRows;
    //
    // Sort rows chronologically by time (0000-2359) when setValue is present (data being applied)
    // console.log(preview, "preview", rowsToReturn);

    return {
      ...ogSection,
      rows: rowsToReturn,
    };
  });
};

const getNMEAKey = (typedCol: ICol, dataRow: Record<string, any>): string => {
  const NMEAKey =
    typedCol.NMEA === "_ROW_DATE_TIME" &&
    "_ROW_DATE_TIME_LT" in dataRow &&
    dataRow._ROW_DATE_TIME_LT
      ? "_ROW_DATE_TIME_LT"
      : (typedCol.NMEA ?? "");
  return NMEAKey;
};

const getNMEARowTimeKey = (row?: INMEARow | null): string => {
  if (!row) return "";

  return (
    row.dateTimeLT ??
    row.timeLT ??
    row.dateTimeUTC ??
    row.timeUTC ??
    row.dateTime ??
    ""
  );
};

const mergeNMEARowsByTime = (
  existingRows: INMEARow[],
  incomingRows: INMEARow[],
): INMEARow[] => {
  const mergedRows = [...existingRows];

  incomingRows.forEach(incomingRow => {
    const incomingTimeKey = getNMEARowTimeKey(incomingRow);
    if (!incomingTimeKey) return;

    const matchingRowIndex = mergedRows.findIndex(
      row => getNMEARowTimeKey(row) === incomingTimeKey,
    );

    const normalizedIncomingRow = {
      ...incomingRow,
      selected: true,
    };

    if (matchingRowIndex !== -1) {
      mergedRows[matchingRowIndex] = {
        ...mergedRows[matchingRowIndex],
        ...normalizedIncomingRow,
      };
      return;
    }

    mergedRows.push(normalizedIncomingRow);
  });

  return sortNMEARowsByTime(mergedRows);
};

/**
 * Filters and retrieves only NMEA filled rows from the provided sections.
 *
 * @param sections - The sections to filter rows from.
 * @param newData - The new data to match against the NMEA rows.
 * @param sectionIndex - The index of the section to process.
 * @param triggeredBy - The trigger type ('add', 'refresh', or undefined for new entry).
 * @returns An array of filtered NMEA sections.
 */
export const getOnlyNMEARows = (
  sections: ISection[],
  newData: Array<Record<string, any>>,
  sectionIndex: number | undefined,
  triggeredBy?: string,
): INMEASection[] => {
  const filteredSections: INMEASection[] = [];
  sections.forEach((section, currentSectionIndex) => {
    // For add mode, only process the specific section where add was clicked
    if (
      triggeredBy === "add" &&
      sectionIndex !== undefined &&
      currentSectionIndex !== sectionIndex
    ) {
      return;
    }

    const newRows: INMEARow[] = [];

    newData.forEach((dataRow, rowIdx) => {
      // For add mode, show all rows but filter out existing ones
      if (triggeredBy === "add" && sectionIndex === currentSectionIndex) {
        // Check if this row already exists in the section by time or NMEA ID
        const rowAlreadyExists = section.rows.some(existingRow => {
          if (!existingRow.cols) return false;

          // Only compare NMEA rows
          const hasNMEAId = Object.values(existingRow.cols).some(
            (col: any) => col.NMEAId,
          );
          if (!hasNMEAId) return false;

          // Check by time
          const timeCol = Object.values(existingRow.cols).find(
            (col: any) => col.NMEA === "_ROW_DATE_TIME",
          );
          if (timeCol?.value) {
            const timeVal = timeCol.value;
            const existingTimeLT =
              typeof timeVal === "object" && timeVal.time
                ? timeVal.time
                : String(timeVal);

            // Compare against incoming LT time (rows are stored in LT)
            const incomingTimeLT = dataRow._ROW_TIME_LT || "";

            if (existingTimeLT === incomingTimeLT) {
              return true;
            }
          }

          return false;
        });

        if (rowAlreadyExists) return;
      } else if (
        (!section.dynamicRows || sectionIndex !== undefined) &&
        rowIdx > 0
      ) {
        return;
      }

      // Use existing NMEA ID from data or create new one if not available
      const nmeaId = dataRow.NMEAId || dataRow._NMEAId || v4();
      const dateTimeObj = {
        dateTime: dataRow._ROW_DATE_TIME || "",
        dateTimeLT: dataRow._ROW_DATE_TIME_LT || "",
        dateTimeUTC: dataRow._ROW_DATE_TIME_UTC || "",
        timeLT:
          dataRow._ROW_TIME_LT?.indexOf(":") !== -1
            ? dataRow._ROW_TIME_LT
            : dataRow._ROW_DATE_TIME_LT?.split(" ")?.[3] || "",
        timeUTC:
          dataRow._ROW_TIME_UTC?.indexOf(":") !== -1
            ? dataRow._ROW_TIME_UTC
            : dataRow._ROW_DATE_TIME_UTC?.split(" ")?.[3] || "",
      };
      const dateTimeValue = dateTimeObj.timeLT || dateTimeObj.dateTime || "";
      let newRow: INMEARow = {
        cols: {},
        NMEAId: nmeaId,
      };

      if (!section.rows[0]) return;
      let hasTimeCol = false;
      Object.values(section.rows[0].cols as Record<string, ICol>).forEach(
        col => {
          const NMEAKey = getNMEAKey(col, dataRow);
          if (col.NMEA && dataRow[NMEAKey]) {
            newRow.cols[col.NMEA] = {
              ...col,
              value:
                col.NMEA === "_ROW_DATE_TIME"
                  ? dateTimeValue
                  : dataRow[NMEAKey],
            };

            if (col.NMEA === "_ROW_DATE_TIME") {
              hasTimeCol = true;
            }
          }
        },
      );
      newRow = { ...newRow, ...(hasTimeCol ? dateTimeObj : {}) };

      if (Object.values(newRow.cols).length > 0) newRows.push(newRow);
    });
    if (
      (sectionIndex !== undefined && currentSectionIndex === sectionIndex) ||
      sectionIndex === undefined
    ) {
      filteredSections.push({
        rows: newRows,
        originalSectionIndex: currentSectionIndex,
      });
    }
  });
  return filteredSections;
};
interface IParseNMEADataForSaving {
  sectionsWithSelectedProp: INMEASection[];
  sections: ISection[];
  NMEAData: INMEASection[] | undefined;
  NMEAModalData: INMEAAutomationModalData | null;
}
export const parseNMEADataForSaving = ({
  sectionsWithSelectedProp,
  sections,
  NMEAModalData,
  NMEAData,
}: IParseNMEADataForSaving) => {
  let newNMEAData: INMEASection[] = sectionsWithSelectedProp;
  if (NMEAModalData?.triggeredBy && NMEAData?.length) {
    newNMEAData = NMEAData.map((s, i) => {
      const parsedSection: INMEASection = structuredClone(s);
      const matchedNewSection = sectionsWithSelectedProp.find(
        el => el.originalSectionIndex === s.originalSectionIndex,
      );
      if (!matchedNewSection) return parsedSection;
      if (NMEAModalData.triggeredBy === "add") {
        return {
          ...parsedSection,
          rows: mergeNMEARowsByTime(parsedSection.rows, matchedNewSection.rows),
        };
      }
      // parsing for refresh trigger
      if (
        NMEAModalData?.sectionIndex === undefined ||
        NMEAModalData?.rowIndex === undefined ||
        NMEAModalData?.sectionIndex !== s.originalSectionIndex
      )
        return parsedSection;

      const logbookRow =
        sections[NMEAModalData.sectionIndex].rows[NMEAModalData.rowIndex];

      if (!logbookRow) return parsedSection;

      const NMEAId = Object.values(
        logbookRow.cols as Record<string, ICol>,
      ).find((col: any) => col?.NMEAId)?.NMEAId;

      if (NMEAId) {
        const matchedNMEARowToUnselect = parsedSection.rows.find(
          r => r && r.NMEAId === NMEAId,
        );
        if (matchedNMEARowToUnselect) {
          matchedNMEARowToUnselect.selected = false;
        }
      }
      return {
        ...parsedSection,
        rows: mergeNMEARowsByTime(parsedSection.rows, matchedNewSection.rows),
      };
    });
  }
  return newNMEAData;
};

/**
 * Cleans up NMEA data by removing selections for rows that no longer exist in the logbook
 * @param nmeaData - Current NMEA data
 * @param sections - Current logbook sections
 * @returns Cleaned NMEA data with invalid selections removed
 */
export const cleanupInvalidNMEASelections = (
  nmeaData: INMEASection[] | undefined,
  sections: ISection[],
): INMEASection[] | undefined => {
  if (!nmeaData) return nmeaData;

  return nmeaData.map(nmeaSection => {
    const section = sections[nmeaSection.originalSectionIndex];
    if (!section?.rows) {
      // If section doesn't exist, unselect all rows
      return {
        ...nmeaSection,
        rows: nmeaSection.rows.map(row => ({
          ...row,
          selected: false,
        })),
      };
    }

    // Check each NMEA row to see if it still corresponds to an existing logbook row
    const cleanedRows = nmeaSection.rows.map(nmeaRow => {
      if (!nmeaRow) return nmeaRow;

      const stillExists = section.rows.some(logbookRow => {
        if (!logbookRow.cols) return false;

        const hasNMEAId = Object.values(logbookRow.cols).some(
          col => col.NMEAId,
        );
        if (!hasNMEAId) return false;

        const time = Object.values(logbookRow.cols).find(
          col => col.NMEA === "_ROW_DATE_TIME",
        )?.value?.time;

        return nmeaRow.timeLT ? time === nmeaRow.timeLT : true;
      });
      return {
        ...nmeaRow,
        selected: stillExists,
      };
    });

    return {
      ...nmeaSection,
      rows: cleanedRows,
    };
  });
};

export const formatDateTimeUTC = (dateTime: any): any => {
  const date = new Date(dateTime);
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  if (date.toString() !== "Invalid Date") {
    return formatter.format(date).replace(",", "");
  } else if (typeof dateTime === "string") {
    return dateTime;
  } else {
    return null;
  }
};
